import React, { useEffect, useState } from "react";
import { listStaff, addStaff } from "@/lib/api";
import { Plus, Check } from "lucide-react";

/** Tappable staff-name selector. `value` is a comma-joined string of names;
 *  `onChange` receives the new comma-joined string. New names are persisted
 *  to the shared staff list when possible (falls back to local-only). */
export default function StaffPicker({ value, onChange }) {
  const [staff, setStaff] = useState([]);
  const selected = (value || "").split(/\s*(?:,|&|\sand\s)\s*/i).map((s) => s.trim()).filter(Boolean);
  const names = Array.from(new Set([...staff, ...selected]));

  useEffect(() => { listStaff().then(setStaff).catch(() => {}); }, []);

  const toggle = (name) => {
    const set = new Set(selected);
    if (set.has(name)) set.delete(name);
    else set.add(name);
    onChange(Array.from(set).join(", "));
  };

  const addNew = async () => {
    const entry = window.prompt("Add a name");
    if (!entry || !entry.trim()) return;
    const name = entry.trim();
    if (!selected.includes(name)) onChange([...selected, name].join(", "));
    try {
      const updated = await addStaff(name);
      setStaff(updated);
    } catch {
      setStaff((s) => Array.from(new Set([...s, name])));
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E8E4DA] p-4" data-testid="staff-picker">
      <label className="text-[10px] tracking-[0.18em] uppercase text-[#5C7566]">Tap who worked this shift</label>
      <div className="flex flex-wrap gap-2 mt-3">
        {names.map((name) => {
          const on = selected.includes(name);
          return (
            <button
              key={`chip-${name}`}
              data-testid={`staff-chip-${name}`}
              onClick={() => toggle(name)}
              className={`px-3.5 h-11 rounded-full text-sm border flex items-center gap-1.5 active:scale-95 transition-colors ${on ? "bg-[#1A5D3A] text-white border-[#1A5D3A]" : "bg-[#F5F2EA] text-[#2B533E] border-[#E8E4DA]"}`}
            >
              {on && <Check size={14} />} {name}
            </button>
          );
        })}
        <button
          data-testid="staff-add"
          onClick={addNew}
          className="px-3.5 h-11 rounded-full text-sm border border-dashed border-[#1A5D3A]/40 text-[#2B533E] flex items-center gap-1.5 active:scale-95"
        >
          <Plus size={14} /> Add name
        </button>
      </div>
      {names.length === 0 && (
        <p className="text-xs text-[#5C7566] mt-3">No staff added yet — tap "Add name" to record who worked.</p>
      )}
    </div>
  );
}
